// Chain clock: estimates the current chain timestamp from observed blocks.
//
// Difficulty decay and scheduled opens are defined in chain time. On a live chain, chain time
// tracks wall time; a local dev chain may run a time-lapse (e.g. anvil_setBlockTimestampInterval
// with 1 s blocks advances chain time ~15x faster than wall time). We record (wall time, block
// timestamp) for each new block and estimate rate = d(chain)/d(wall) over a recent window, then
// extrapolate from the newest block. The estimate is monotonic between blocks.

export interface ClockSample {
  block: bigint;
  /** block timestamp, seconds */
  chain: number;
  /** wall time the block was observed, ms */
  wall: number;
}

export interface ChainClockOptions {
  windowMs: number;
  maxSamples: number;
  /** ignore block pairs observed closer together than this (poll jitter) */
  minIntervalMs: number;
  minRate: number;
  maxRate: number;
}

const DEFAULTS: ChainClockOptions = { windowMs: 120_000, maxSamples: 40, minIntervalMs: 300, minRate: 0.1, maxRate: 120 };

export class ChainClockEstimator {
  private samples: ClockSample[] = [];
  private lastReturned = 0;
  private readonly opts: ChainClockOptions;

  constructor(opts: Partial<ChainClockOptions> = {}) {
    this.opts = { ...DEFAULTS, ...opts };
  }

  /** Records a block. Returns true if it is newer than anything seen (resets on chain rewinds). */
  observe(block: bigint, chainTs: number, wallMs: number): boolean {
    const last = this.samples[this.samples.length - 1];
    if (last && block <= last.block) {
      if (block < last.block || chainTs < last.chain) this.reset(); // chain restarted or reorged
      else return false;
    }
    this.samples.push({ block, chain: chainTs, wall: wallMs });
    const cutoff = wallMs - this.opts.windowMs;
    while (this.samples.length > 2 && (this.samples.length > this.opts.maxSamples || this.samples[0]!.wall < cutoff)) {
      this.samples.shift();
    }
    return true;
  }

  reset(): void {
    this.samples = [];
    this.lastReturned = 0;
  }

  get latest(): ClockSample | null {
    return this.samples[this.samples.length - 1] ?? null;
  }

  /**
   * Chain seconds per wall second (1 when unknown). Uses the intervals between observed blocks:
   * intervals far above the median (a one-off evm_increaseTime / evm_setNextBlockTimestamp jump)
   * are dropped, the rest are pooled so polling jitter averages out.
   */
  get rate(): number {
    const iv: { dc: number; dw: number; r: number }[] = [];
    for (let i = 1; i < this.samples.length; i++) {
      const a = this.samples[i - 1]!;
      const b = this.samples[i]!;
      const dw = (b.wall - a.wall) / 1000;
      if (dw * 1000 < this.opts.minIntervalMs) continue;
      iv.push({ dc: b.chain - a.chain, dw, r: (b.chain - a.chain) / dw });
    }
    // one interval cannot tell a time-lapse from a one-off warp; overestimating chain time is the
    // unsafe direction (a proof would look valid too early), so wait for a second interval
    if (iv.length < 2) return 1;
    const sorted = iv.map((x) => x.r).sort((x, y) => x - y);
    const median = sorted[Math.floor((sorted.length - 1) / 2)]!; // lower median
    let dc = 0;
    let dw = 0;
    for (const x of iv) {
      if (x.r > Math.max(3 * median, median + 2)) continue;
      dc += x.dc;
      dw += x.dw;
    }
    const r = dw > 0 ? dc / dw : 1;
    if (!Number.isFinite(r) || r <= 0) return 1;
    return Math.min(this.opts.maxRate, Math.max(this.opts.minRate, r));
  }

  /** Estimated current chain timestamp in (fractional) seconds, or null before the first block. */
  now(wallMs: number = Date.now()): number | null {
    const last = this.latest;
    if (!last) return null;
    const est = last.chain + (Math.max(0, wallMs - last.wall) / 1000) * this.rate;
    // never step backwards between blocks (a new block may land slightly "earlier" than extrapolated)
    if (est < this.lastReturned && this.lastReturned - est < 60) return this.lastReturned;
    this.lastReturned = est;
    return est;
  }
}
